import { queryOptions } from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import { supabase } from '@/lib/supabase/client'

export type OrganizationMembership =
  Database['public']['Tables']['organization_memberships']['Row']

/**
 * Organization role constants
 *
 * Single source of truth for valid organization roles.
 * Add new roles here to extend role permissions across the application.
 *
 * NOTE: supabase/functions/_shared/organization-roles.ts must be kept in sync
 * (Deno edge functions cannot import from src directory)
 */
export const ORGANIZATION_ROLES = ['org_admin', 'member'] as const
export type OrganizationRole = (typeof ORGANIZATION_ROLES)[number]

/**
 * Organization member with profile details
 * profiles is non-nullable due to !inner join
 */
export interface OrganizationMemberWithProfile extends OrganizationMembership {
  profiles: {
    email: string
    full_name: string | null
    avatar_url: string | null
  }
}

/**
 * User's organization membership with organization details
 * organizations is non-nullable due to !inner join
 */
export interface UserMembershipWithOrg extends OrganizationMembership {
  organizations: {
    id: string
    name: string
  }
}

export const organizationMembership = {
  /**
   * Find all organization memberships for a specific user.
   *
   * Used to determine which organizations the user has access to.
   * This is the single source of truth for redirect logic in index/admin routes.
   *
   * Note: Explicit userId filter is required because super admins can see all
   * memberships via RLS, but we only want to return the user's own memberships.
   */
  findManyByUser: (userId: string) =>
    queryOptions({
      queryKey: ['organization-memberships', 'user', userId],
      queryFn: async (): Promise<UserMembershipWithOrg[]> => {
        if (!userId || userId.trim() === '') {
          throw new Error(
            'Invalid userId: cannot fetch memberships without valid user ID'
          )
        }

        const { data, error } = await supabase
          .from('organization_memberships')
          .select('*, organizations!inner(id, name)')
          .eq('user_id', userId) // Explicit filter (required for super admins)
          .is('organizations.deactivated_at', null) // Only active orgs
          .order('created_at', { ascending: true })

        if (error) {
          throw new Error(
            `Failed to fetch user memberships: ${error.message}`,
            {
              cause: error,
            }
          )
        }

        return (data || []) as unknown as UserMembershipWithOrg[]
      },
      staleTime: 5 * 60 * 1000,
    }),

  /**
   * Find all members of a specific organization with their profile details.
   *
   * Access control (enforced by RLS):
   * - Super admins: Can access ANY organization's members
   * - Org members: Can only access members of organizations they belong to
   * - Non-members: Cannot access any members
   *
   * Note: Regular org members (not just admins) can see other members in their organizations.
   * This is enforced server-side via RLS policies.
   */
  findManyByOrg: (orgId: string) =>
    queryOptions({
      queryKey: ['organization-memberships', 'org', orgId],
      queryFn: async (): Promise<OrganizationMemberWithProfile[]> => {
        if (!orgId || orgId.trim() === '') {
          throw new Error(
            'Invalid orgId: cannot fetch members without valid organization ID'
          )
        }

        const { data, error } = await supabase
          .from('organization_memberships')
          .select('*, profiles!inner(email, full_name, avatar_url)')
          .eq('org_id', orgId)
          .order('profiles(full_name)', { ascending: true, nullsFirst: false })
          .order('profiles(email)', { ascending: true })

        if (error) {
          throw new Error(
            `Failed to fetch organization members: ${error.message}`,
            {
              cause: error,
            }
          )
        }

        return (data || []) as unknown as OrganizationMemberWithProfile[]
      },
      // Cache for 5 minutes (admin dashboards don't need real-time data)
      staleTime: 5 * 60 * 1000,
    }),

  /**
   * Find a specific user's membership in an organization.
   *
   * Used to determine the current user's role (org_admin or member) within an organization.
   * This is typically used for authorization checks and conditional UI rendering.
   *
   * Access control (enforced by RLS):
   * - Super admins: Can access ANY user's membership
   * - Users: Can only access their own memberships
   */
  findOneByUserAndOrg: (userId: string, orgId: string) =>
    queryOptions({
      queryKey: ['organization-memberships', 'user', userId, 'org', orgId],
      queryFn: async () => {
        if (!userId || userId.trim() === '') {
          throw new Error(
            'Invalid userId: cannot fetch membership without valid user ID'
          )
        }

        if (!orgId || orgId.trim() === '') {
          throw new Error(
            'Invalid orgId: cannot fetch membership without valid organization ID'
          )
        }

        const { data, error } = await supabase
          .from('organization_memberships')
          .select('*')
          .eq('user_id', userId)
          .eq('org_id', orgId)
          .maybeSingle()

        if (error) {
          throw new Error(
            `Failed to fetch organization membership: ${error.message}`,
            {
              cause: error,
            }
          )
        }

        return data
      },
      // Cache for 5 minutes (role changes are infrequent)
      staleTime: 5 * 60 * 1000,
    }),
}
